import React from "react";
import type { GetServerDataProps } from "gatsby";

import { Button, RichText, Container, MetaComponent } from "@bared/ui";

import { PageAccountWishlistSchema, filterProjection } from "@bared/groq";

import type { HeadProps, PageProps } from "~/types/gatsby";

import { seoConfigForPage, getSeoMeta } from "~/lib/seo";
import { contextFromServerDataProps } from "~/lib/server";

import { useWishlistProducts } from "~/hooks/useWishlist";

import { ProductCard } from "~/components/ProductCard";

import { AnalyticsViewItemListProvider } from "~/providers/AnalyticsViewItemListProvider";
import { Sections } from "~/components/Sections/Sections";

const pageQuery = filterProjection(
  PageAccountWishlistSchema,
  `_type == "pageAccountWishlist"`,
);

export async function getServerData(props: GetServerDataProps) {
  // Resolve context.
  const context = await contextFromServerDataProps(props);

  // Expand context.
  const { respond, notFound, groq, sectionResolver } = context;

  // Fetch pages from sanity.
  const page = await groq(pageQuery);

  // Page not found in Sanity, return 404.
  if (!page) return notFound();

  // Resolve seo config.
  const seo = seoConfigForPage({
    context,
    sanityMetadata: page.metadata,
  });

  // Resolve sections.
  if (page.flexibleSections) {
    page.flexibleSections = await sectionResolver.resolve({
      sanitySections: page.flexibleSections,
    });
  }

  // Create a minimal page object with only what we need
  const _page = {
    flexibleSections: page.flexibleSections ?? [],
  };

  // Respond with server data
  return respond({ page: _page, seo } as const);
}

export type ServerData = Awaited<ReturnType<typeof getServerData>>["props"];

export default function Page({ serverData }: PageProps<ServerData>) {
  if (200 === serverData.status) {
    return (
      <div className="bg-neutral">
        <Container maxWidth="xl" className="py-midi md:py-mega">
          <div className="flex flex-col items-center gap-mini md:gap-maxi">
            <PageInner />
          </div>
        </Container>

        {serverData.page.flexibleSections && (
          <Sections page={{ content: serverData.page.flexibleSections }} />
        )}
      </div>
    );
  }

  return null;
}

function PageInner() {
  const { products, isPending } = useWishlistProducts();

  if (!isPending && 0 === products.length) {
    return (
      <>
        <RichText className="text-center">
          <h1>You have no items on your wishlist... yet.</h1>
        </RichText>
        <Button href="/" size="large">
          Start shopping
        </Button>
      </>
    );
  }

  if (0 < products.length) {
    return (
      <>
        <RichText className="text-center">
          <h1>My Wishlist</h1>
          <p>
            {products.length} product{1 < products.length && "s"}
          </p>
        </RichText>
        <AnalyticsViewItemListProvider
          itemListId="wishlist"
          itemListName="Wishlist"
        >
          <div className="grid grid-cols-2 gap-mini md:grid-cols-4 md:gap-maxi">
            {products.map((product, index) => (
              <ProductCard
                {...product}
                index={index}
                key={product.handle}
                sizes="(max-width: 768px) 50vw, 25vw"
              />
            ))}
          </div>
        </AnalyticsViewItemListProvider>
      </>
    );
  }

  if (isPending) {
    return (
      <RichText className="text-center">
        <h1>Loading your wishlist...</h1>
      </RichText>
    );
  }

  return null;
}

export function Head({ serverData }: HeadProps<ServerData>) {
  if (!serverData) return null;

  return (
    <MetaComponent
      meta={getSeoMeta(serverData.context.rootData.seo, serverData.seo)}
    />
  );
}
